import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { siteMeta } from '../../data/navigation'
import { FallingPattern } from '../ui/falling-pattern'
import Parallax from '../ui/Parallax'

export default function HeroSection() {
  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      aria-label="Hero"
    >
      {/* Falling pattern background */}
      <Parallax speed={0.08} className="absolute inset-0 opacity-30">
        <FallingPattern
          color="var(--electric-blue)"
          backgroundColor="#030712"
          duration={180}
          blurIntensity="0.6em"
          density={1.5}
          className="h-full w-full [mask-image:radial-gradient(ellipse_at_center,transparent_30%,black_70%)]"
        />
      </Parallax>

      {/* Gradient orbs overlay */}
      <Parallax speed={0.15} className="absolute top-1/4 -left-32 w-[500px] h-[500px]">
        <div className="w-full h-full rounded-full bg-electric-blue/20 blur-[120px] animate-float" />
      </Parallax>
      <Parallax speed={0.12} className="absolute bottom-1/4 -right-32 w-[400px] h-[400px]">
        <div className="w-full h-full rounded-full bg-electric-purple/20 blur-[120px] animate-float" style={{ animationDelay: '-3s' }} />
      </Parallax>
      <Parallax speed={0.05} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]">
        <div className="w-full h-full rounded-full bg-premium-600/10 blur-[150px]" />
      </Parallax>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block mb-6 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-electric-blue bg-electric-blue/10 rounded-full border border-electric-blue/20">
                Premium Digital Agency
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] tracking-tight"
            >
              We Build{' '}
              <span className="bg-gradient-to-r from-electric-blue via-electric-purple to-violet-400 bg-clip-text text-transparent">
                Digital Excellence
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-6 text-lg md:text-xl text-gray-400 leading-relaxed max-w-lg"
            >
              {siteMeta.description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                to="/portfolio"
                className="px-8 py-3.5 text-base font-semibold text-white rounded-full bg-gradient-to-r from-electric-blue to-electric-purple hover:shadow-lg hover:shadow-electric-blue/25 transition-all duration-300 hover:scale-105"
              >
                View Our Work
              </Link>
              <Link
                to="/contact"
                className="px-8 py-3.5 text-base font-semibold text-white rounded-full border border-white/10 hover:border-white/20 bg-white/5 hover:bg-white/10 transition-all duration-300"
              >
                Let's Talk
              </Link>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-12 flex items-center gap-6 text-sm text-gray-500"
            >
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-8 h-8 rounded-full border-2 border-gray-950 bg-gradient-to-br from-premium-400 to-premium-600"
                  />
                ))}
              </div>
              <span>
                Trusted by <strong className="text-white">50+</strong> brands
                worldwide
              </span>
            </motion.div>
          </div>

          {/* Visual / Logo display */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:flex items-center justify-center relative"
          >
            <div className="relative">
              {/* Glow ring */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-electric-blue/30 to-electric-purple/30 blur-[60px] animate-pulse" />
              {/* Logo display */}
              <div className="relative w-80 h-80 rounded-full border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-xl flex items-center justify-center">
                <img
                  src={siteMeta.logo}
                  alt={siteMeta.name}
                  className="w-56 h-56 object-contain"
                  onError={(e) => {
                    ;(e.target as HTMLImageElement).style.display = 'none'
                    const parent = (e.target as HTMLImageElement)
                      .parentElement
                    if (parent) {
                      parent.innerHTML = `<div class="text-7xl font-bold bg-gradient-to-r from-electric-blue to-electric-purple bg-clip-text text-transparent">SST</div>`
                    }
                  }}
                />
              </div>
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-electric-blue to-cyan-400 blur-sm animate-float" />
              <div className="absolute -bottom-2 -left-6 w-12 h-12 rounded-xl bg-gradient-to-br from-electric-purple to-violet-400 blur-sm animate-float" style={{ animationDelay: '-2s' }} />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
